/**
 * Section 9 · Upload Data — Sprint P37r · Phase 2 (Feb 2026).
 *
 * Inline drag-and-drop upload staging for the Enterprise Create-Project
 * Wizard. On the first successful drop we materialise a temporary
 * ``status=draft`` project via ``POST /api/setup/draft`` and route all
 * subsequent uploads to that draft's project-id. When the wizard user
 * presses "Create Project" the draft is *promoted* in place — no data
 * migration required, no orphan uploads.
 *
 * Data flow:
 *   1. Ensure draft (idempotent) → returns draft_project_id
 *   2. Upload file via existing kind-specific endpoint
 *   3. Poll ``GET /api/setup/draft/{pid}/status`` for live counts
 *   4. Wizard's Create action calls /setup/create with draft_project_id
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CheckCircle, CloudArrowUp, FileCsv, Info, MapTrifold, Mountains,
  TrashSimple, Warning, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CardShell } from "./atoms";

/* ── Dataset catalogue ─────────────────────────────────────────── */
const DRILLHOLE_KINDS = [
  { kind: "collar",    label: "Collar",     required: true,
    hint: "hole_id, x, y, z, depth",       accept: ".csv" },
  { kind: "survey",    label: "Survey",     required: false,
    hint: "hole_id, depth, azimuth, dip",  accept: ".csv" },
  { kind: "assay",     label: "Assay",      required: true,
    hint: "hole_id, from, to, grade",      accept: ".csv" },
  { kind: "geology",   label: "Lithology",  required: false,
    hint: "hole_id, from, to, lithology",  accept: ".csv" },
  { kind: "density",   label: "Density",    required: false,
    hint: "hole_id, from, to, density",    accept: ".csv" },
  { kind: "qaqc",      label: "QA / QC",    required: false,
    hint: "sample_id, grade (+ CRM/blank/duplicate)", accept: ".csv" },
];

/* ── Format catalogue (footer reference) ───────────────────────── */
const NATIVE_FORMATS = [
  "Collar / Survey / Assay / Lithology / Density CSV",
  "Boundary — KML · KMZ · SHP (ZIP) · GeoJSON",
  "Topography — GeoTIFF · ASC · XYZ",
  "Block Model CSV (X,Y,Z,grade,…)",
];
const EXCHANGE_ONLY = [
  ["Leapfrog Geo", "Export to OBJ / VTK / GeoTIFF / CSV block table"],
  ["Vulcan",       ".00t block model → CSV. DXF for solids."],
  ["Datamine",     ".dm block model → CSV. DXF/OBJ for wireframes."],
  ["Surpac",       ".str/.dtm supported via DXF export."],
  ["Micromine",    "Export to DXF or CSV block table."],
];

/* ── Small utility to POST with FormData + return validation ───── */
async function uploadFile(url, file, extraForm = {}) {
  const fd = new FormData();
  fd.append("file", file);
  Object.entries(extraForm).forEach(([k, v]) => fd.append(k, v));
  const r = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data;
}

/* ══════════════════════════════════════════════════════════════════
 *  Section component
 * ═════════════════════════════════════════════════════════════════ */
export default function Section9Uploads({
  form, set, onUploadsChange,
}) {
  const qc = useQueryClient();
  const draftId = form?.draft_project_id;

  /* ── Ensure-draft: idempotent creation with minimum identity ─── */
  const ensureDraftMut = useMutation({
    mutationFn: async () => {
      if (draftId) return { draft_project_id: draftId };
      const r = await api.post("/setup/draft", {
        name:               form.name || "Untitled Project (Draft)",
        country:            form.country,
        commodity:          form.commodity,
        deposit_type:       form.deposit_type,
        reporting_standard: form.reporting_standard,
        latitude:           form.latitude,
        longitude:          form.longitude,
      });
      return r.data;
    },
    onSuccess: (r) => {
      if (r?.draft_project_id && !draftId) {
        set?.({ draft_project_id: r.draft_project_id });
      }
    },
    onError:  () => toast.error("Could not create upload staging area — try again in a moment."),
  });

  /* ── Draft status poll (only when draftId exists) ────────────── */
  const statusQ = useQuery({
    queryKey: ["draft-status", draftId],
    queryFn:  () => api.get(`/setup/draft/${draftId}/status`).then((r) => r.data),
    enabled:  !!draftId,
    refetchInterval: 4000,
    staleTime: 2000,
  });

  useEffect(() => {
    if (statusQ.data && onUploadsChange) onUploadsChange(statusQ.data);
  }, [statusQ.data, onUploadsChange]);

  /* ── Upload state, one entry per kind ─────────────────────────── */
  const [inflight, setInflight] = useState({});
  const [errors,   setErrors]   = useState({});

  const doUpload = useCallback(async (kind, file, url, extra) => {
    if (!file) return;
    // Guard: create draft if missing (blocking)
    let pid = draftId;
    if (!pid) {
      const r = await ensureDraftMut.mutateAsync();
      pid = r?.draft_project_id;
      if (!pid) return;
    }
    setInflight((m) => ({ ...m, [kind]: file.name }));
    setErrors((m)   => { const { [kind]: _, ...rest } = m; return rest; });
    try {
      const target = url.replace("{pid}", pid);
      const result = await uploadFile(target, file, extra);
      // File-parsing errors come back with `validation.ok=false` on the CSV endpoint.
      const v = result?.validation;
      if (v && v.ok === false) {
        setErrors((m) => ({ ...m, [kind]:
          v.missing?.length ? `Missing columns: ${v.missing.join(", ")}`
                            : "Validation failed" }));
        toast.error(`${kind.toUpperCase()} · validation failed`);
      } else {
        toast.success(`${kind.toUpperCase()} uploaded · ${result.rows ?? "OK"}`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Upload failed";
      setErrors((m) => ({ ...m, [kind]: String(detail) }));
      toast.error(`${kind.toUpperCase()} upload failed`);
    } finally {
      setInflight((m) => { const { [kind]: _, ...rest } = m; return rest; });
      qc.invalidateQueries({ queryKey: ["draft-status", pid] });
    }
  }, [draftId, ensureDraftMut, qc]);

  /* ── Delete a staged dataset (currently only reset draft) ────── */
  const cancelDraftMut = useMutation({
    mutationFn: () => api.delete(`/setup/draft/${draftId}`).then((r) => r.data),
    onSuccess: () => {
      set?.({ draft_project_id: undefined });
      qc.removeQueries({ queryKey: ["draft-status"] });
      toast.success("Uploads cleared · staging draft removed.");
    },
    onError: () => toast.error("Could not clear staging draft."),
  });

  const counts   = statusQ.data?.counts   || {};
  const columns  = statusQ.data?.columns  || {};
  const boundary = statusQ.data?.boundary;
  const dem      = statusQ.data?.dem;

  return (
    <CardShell number={9} title="Upload Data · Inline Staging"
                icon={CloudArrowUp} testId="section-uploads" id="s9"
                subtitle="Drag or click to attach each dataset. Files stage against a temporary draft and are promoted to the real project on Create.">
      {/* Draft indicator */}
      <div className="rounded-md border border-brand-blue/25 bg-brand-blue/5 px-3 py-2 text-[12px] text-slate-700 flex items-start gap-2"
              data-testid="uploads-draft-banner">
        <Info size={13} className="text-brand-blue mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          {draftId ? (
            <>
              <b>Draft ready.</b> Uploads are being staged against draft
              <span className="font-mono text-brand-blue"> {draftId.slice(0, 12)}…</span>
              &nbsp;— the draft is promoted to a real project when you press
              <span className="font-mono"> Create Project</span>. Drafts auto-purge after 7 days.
            </>
          ) : (
            <>
              <b>Drop a file below to begin.</b> A temporary staging draft is
              created automatically on the first upload so validation runs
              live and no data is lost between now and pressing
              <span className="font-mono"> Create Project</span>.
            </>
          )}
        </div>
        {draftId && (
          <Button variant="ghost" size="sm" onClick={() => cancelDraftMut.mutate()}
                     disabled={cancelDraftMut.isPending}
                     className="h-7 text-[11px] gap-1.5 text-rose-700 hover:text-rose-800"
                     data-testid="btn-cancel-draft">
            <TrashSimple size={11} /> Clear
          </Button>
        )}
      </div>

      {/* Drillhole grid */}
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
        {DRILLHOLE_KINDS.map((c) => (
          <DropZone
            key={c.kind}
            kind={c.kind}
            label={c.label}
            hint={c.hint}
            accept={c.accept}
            required={c.required}
            count={counts[c.kind] || 0}
            columns={columns[c.kind] || []}
            inflight={inflight[c.kind]}
            error={errors[c.kind]}
            testId={`upload-${c.kind}`}
            onFile={(f) => doUpload(c.kind, f,
              `/projects/{pid}/upload/${c.kind}`, {})}
          />
        ))}
      </div>

      {/* GIS + DEM row */}
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
        <DropZone
          kind="boundary"
          label="Project Boundary"
          hint="KML · KMZ · SHP (ZIP) · GeoJSON · GPX · CSV"
          accept=".kml,.kmz,.zip,.geojson,.json,.gpx,.csv"
          icon={MapTrifold}
          required={false}
          count={boundary ? 1 : 0}
          columns={boundary ? [`${boundary.filename || "boundary"}${boundary.area_km2 ? ` · ${Number(boundary.area_km2).toFixed(2)} km²` : ""}`] : []}
          inflight={inflight.boundary}
          error={errors.boundary}
          testId="upload-boundary"
          onFile={(f) => doUpload("boundary", f,
            "/projects/{pid}/gis/boundary/upload", { source_crs: "EPSG:4326" })}
        />
        <DropZone
          kind="dem"
          label="Topography / DEM"
          hint="GeoTIFF · ASC · XYZ · TIF"
          accept=".tif,.tiff,.geotiff,.asc,.xyz,.csv"
          icon={Mountains}
          required={false}
          count={dem ? 1 : 0}
          columns={dem ? [`${dem.name || "surface"}${dem.n_vertices ? ` · ${Number(dem.n_vertices).toLocaleString()} verts` : ""}`] : []}
          inflight={inflight.dem}
          error={errors.dem}
          testId="upload-dem"
          onFile={(f) => doUpload("dem", f,
            "/projects/{pid}/geotiff/upload", { subsample: 10 })}
        />
      </div>

      {/* Format reference footer */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-1">Native formats · direct upload</div>
          <ul className="space-y-0.5 text-[11.5px] text-slate-600">
            {NATIVE_FORMATS.map((f) => (
              <li key={f} className="flex items-center gap-1.5"><CheckCircle size={11} className="text-emerald-500" weight="fill" />{f}</li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-1">Proprietary block-model formats · exchange export required</div>
          <ul className="space-y-0.5 text-[11.5px] text-slate-600">
            {EXCHANGE_ONLY.map(([name, note]) => (
              <li key={name} className="flex items-start gap-1.5">
                <Warning size={11} className="text-amber-500 mt-0.5" weight="fill" />
                <span><b className="text-slate-700">{name}</b> · {note}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </CardShell>
  );
}

/* ══════════════════════════════════════════════════════════════════
 *  DropZone — a single drag/click upload target
 * ═════════════════════════════════════════════════════════════════ */
function DropZone({
  kind, label, hint, accept, required, icon: Icon = FileCsv,
  count, columns, inflight, error, onFile, testId,
}) {
  const inputRef = useRef(null);
  const [hover, setHover] = useState(false);

  const onPick = () => inputRef.current?.click();
  const onDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setHover(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) onFile(f);
  }, [onFile]);

  const status = useMemo(() => {
    if (inflight)     return { tone: "amber",   label: `Uploading · ${inflight}` };
    if (error)        return { tone: "rose",    label: error };
    if (count > 0)    return { tone: "emerald", label: `${count.toLocaleString()} row${count === 1 ? "" : "s"} staged` };
    return             { tone: "slate",   label: required ? "Required · not uploaded" : "Optional" };
  }, [inflight, error, count, required]);

  const toneCls = {
    emerald: "border-emerald-200 bg-emerald-50/50 text-emerald-700",
    amber:   "border-amber-200 bg-amber-50/50 text-amber-800",
    rose:    "border-rose-200 bg-rose-50/50 text-rose-700",
    slate:   "border-slate-200 bg-slate-50 text-slate-500",
  }[status.tone];

  return (
    <div
      data-testid={testId}
      onDragEnter={(e) => { e.preventDefault(); setHover(true); }}
      onDragOver={(e)  => { e.preventDefault(); setHover(true); }}
      onDragLeave={()  => setHover(false)}
      onDrop={onDrop}
      onClick={onPick}
      className={`group cursor-pointer relative rounded-md border-2 border-dashed transition-all
                     px-3 py-2.5 flex items-center gap-3
                     ${hover ? "border-brand-blue bg-brand-blue/5" : "border-slate-200 bg-white hover:border-brand-blue/40"}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = "";
        }}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      <div className={`w-9 h-9 shrink-0 rounded-md border flex items-center justify-center ${count > 0 ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
        <Icon size={16} className={count > 0 ? "text-emerald-600" : "text-slate-500"} weight="duotone" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-heading font-bold text-[12.5px] text-slate-900 truncate">
            {label}
          </div>
          {required && (
            <span className="text-[9px] uppercase tracking-widest text-rose-700 bg-rose-50 border border-rose-200 rounded-sm px-1 py-0.5">
              Required
            </span>
          )}
        </div>
        <div className="text-[10.5px] font-mono text-slate-500 truncate">{hint}</div>
        {columns?.length > 0 && (
          <div className="text-[10.5px] text-slate-500 truncate mt-0.5"
                  data-testid={`${testId}-columns`}>
            <span className="text-slate-400">cols:</span> {columns.slice(0, 6).join(", ")}
            {columns.length > 6 && <span className="text-slate-400">…</span>}
          </div>
        )}
      </div>
      <div className={`text-[10px] uppercase tracking-widest border rounded-sm px-1.5 py-0.5 shrink-0 ${toneCls}`}
              data-testid={`${testId}-status`}>
        {inflight ? "Uploading" : (count > 0 ? "Uploaded" : (error ? "Error" : "Empty"))}
      </div>
      {(error || status.tone === "slate") && (
        <div className="absolute -bottom-0.5 left-3 right-3 pointer-events-none">
          {error && (
            <div className="text-[10.5px] text-rose-600 bg-white/95 rounded px-1 truncate">
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
